import React, { useState, useMemo, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { ProjectInfo, GlobalUser } from '../src/types';
import { PowerBIEmbedContainer } from '../src/components/analytics/PowerBIEmbedContainer';
import {
    fetchPowerBiReportsCatalog,
    registerPowerBiReport,
    deletePowerBiReport,
    runPowerBiDiagnostic
} from '../src/services/powerBiService';

interface ReportsProps {
    projectInfo: ProjectInfo;
    currentUser?: GlobalUser;
    hasPermission?: (module: string, action: 'view' | 'edit') => boolean;
}

type TabType = 'home' | 'catalog' | 'viewer' | 'designer' | 'admin' | 'diagnose';

interface ReportItem {
    id: string;
    powerbi_report_id: string;
    powerbi_dataset_id: string;
    powerbi_workspace_id: string;
    name: string;
    scope: 'obra' | 'multiobra' | 'corporativo';
    metadata?: {
        category?: string;
        permissions?: string[];
        description?: string;
    };
    created_by?: string;
    created_at?: string;
    project_ids?: string[];
}

export default function ReportsScreen({
    projectInfo,
    currentUser,
    hasPermission = () => false
}: ReportsProps) {
    const { instance, accounts } = useMsal();
    const [activeTab, setActiveTab] = useState<TabType>('home');

    const getFreshMsalToken = async () => {
        const account = instance.getActiveAccount() || accounts[0];
        if (account) {
            try {
                const response = await instance.acquireTokenSilent({
                    scopes: ["User.Read"],
                    account: account
                });
                localStorage.setItem('klyd_msal_access_token', response.accessToken);
                return response.accessToken;
            } catch (e) {
                console.warn("Silent token acquisition failed:", e);
            }
        }
        return null;
    };

    // --- Catálogo e Infraestructura ---
    const [reportsList, setReportsList] = useState<ReportItem[]>([]);
    const [projectsList, setProjectsList] = useState<any[]>([]);
    const [datasetsList, setDatasetsList] = useState<any[]>([]);
    const [loadingCatalog, setLoadingCatalog] = useState<boolean>(false);
    const [catalogError, setCatalogError] = useState<string | null>(null);

    // --- Categorías Corporativas (Módulos K-LyDApp) ---
    const [categories, setCategories] = useState<string[]>([
        'Planificación',
        'Calidad',
        'Subcontratos',
        'SSOMA'
    ]);
    const [newCategoryName, setNewCategoryName] = useState<string>('');

    // --- Reporte Seleccionado y Contenedor ---
    const [selectedReport, setSelectedReport] = useState<ReportItem | null>(null);
    const [viewerMode, setViewerMode] = useState<'view' | 'edit'>('view');
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
    const [embedRefreshKey, setEmbedRefreshKey] = useState<number>(0);

    // --- Formulario Diseñador ---
    const [designName, setDesignName] = useState<string>('');
    const [designDatasetId, setDesignDatasetId] = useState<string>('');
    const [designScope, setDesignScope] = useState<'obra' | 'multiobra' | 'corporativo'>('obra');
    const [designCategory, setDesignCategory] = useState<string>('Planificación');
    const [designProjects, setDesignProjects] = useState<string[]>([]);
    const [isDesigning, setIsDesigning] = useState<boolean>(false);

    // --- Edición en Administración ---
    const [editingReportId, setEditingReportId] = useState<string | null>(null);
    const [editName, setEditName] = useState<string>('');
    const [editScope, setEditScope] = useState<'obra' | 'multiobra' | 'corporativo'>('obra');
    const [editCategory, setEditCategory] = useState<string>('Planificación');
    const [editProjects, setEditProjects] = useState<string[]>([]);
    const [editPermissions, setEditPermissions] = useState<string[]>(['rep_reportes']);

    // --- Favoritos y Recientes ---
    const [favorites, setFavorites] = useState<string[]>([]);
    const [recentOpened, setRecentOpened] = useState<string[]>([]);

    // --- Diagnóstico Técnico ---
    const [diagnosing, setDiagnosing] = useState<boolean>(false);
    const [diagnosticData, setDiagnosticData] = useState<any | null>(null);

    // --- Filtros & Búsqueda ---
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [categoryFilter, setCategoryFilter] = useState<string>('todos');
    const [groupByScope, setGroupByScope] = useState<boolean>(false);

    const emailKey = currentUser?.email ? currentUser.email.toLowerCase() : 'guest';

    // Cargar Favoritos y Recientes
    useEffect(() => {
        const storedFavs = localStorage.getItem(`klyd_favs_${emailKey}`);
        if (storedFavs) {
            try { setFavorites(JSON.parse(storedFavs)); } catch (e) { console.error(e); }
        }
        const storedRecents = localStorage.getItem(`klyd_recents_${emailKey}`);
        if (storedRecents) {
            try { setRecentOpened(JSON.parse(storedRecents)); } catch (e) { console.error(e); }
        }
    }, [emailKey]);

    const getReportCategory = (name?: string): string => {
        const n = (name || '').toLowerCase();
        if (n.includes('curva') || n.includes('avance') || n.includes('hito') || n.includes('planific') || n.includes('ruta')) {
            return 'Planificación';
        }
        if (n.includes('calidad') || n.includes('protocolo') || n.includes('revisor') || n.includes('cnc')) {
            return 'Calidad';
        }
        if (n.includes('pago') || n.includes('financiero') || n.includes('costo') || n.includes('subcontrat')) {
            return 'Subcontratos';
        }
        if (n.includes('prevenc') || n.includes('ssoma') || n.includes('incidente') || n.includes('charla') || n.includes('seguridad')) {
            return 'SSOMA';
        }
        return 'Planificación';
    };

    // Guardar/Desmarcar Favoritos
    const toggleFavorite = (reportId: string) => {
        const updatedFavs = favorites.includes(reportId)
            ? favorites.filter(id => id !== reportId)
            : [...favorites, reportId];
        setFavorites(updatedFavs);
        localStorage.setItem(`klyd_favs_${emailKey}`, JSON.stringify(updatedFavs));
    };

    // Registrar Recientes
    const registerRecentOpen = (reportId: string) => {
        const filtered = recentOpened.filter(id => id !== reportId);
        const updatedRecents = [reportId, ...filtered].slice(0, 5);
        setRecentOpened(updatedRecents);
        localStorage.setItem(`klyd_recents_${emailKey}`, JSON.stringify(updatedRecents));
    };

    // Cargar Catálogo
    const loadCatalogData = async () => {
        setLoadingCatalog(true);
        setCatalogError(null);
        try {
            await getFreshMsalToken();
            const data = await fetchPowerBiReportsCatalog();
            const rawReports = data.reports || [];
            const hasLocalReport = rawReports.some((r: any) => r.powerbi_report_id === 'local-simulated-report-id');
            const finalReports = hasLocalReport ? rawReports : [
                {
                    id: 'local-simulated-report-id',
                    powerbi_report_id: 'local-simulated-report-id',
                    powerbi_dataset_id: 'local-dataset-id',
                    powerbi_workspace_id: 'local-workspace-id',
                    name: 'Reporte de Gestión de Obra (Local)',
                    scope: 'obra' as const,
                    created_by: 'sistema',
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                    metadata: {
                        category: 'Planificación',
                        description: 'Reporte analítico local basado en los datos reales del proyecto.'
                    },
                    project_ids: [projectInfo.id]
                },
                ...rawReports
            ];
            setReportsList(finalReports);
            setProjectsList(data.projects || []);

            const rawDatasets = data.datasets || [];
            const hasLocalDs = rawDatasets.some((d: any) => d.id === 'local-dataset-id');
            const finalDatasets = hasLocalDs ? rawDatasets : [
                { id: 'local-dataset-id', name: 'K-LyDapp' },
                ...rawDatasets
            ];
            setDatasetsList(finalDatasets);
            
            // Extraer categorías encontradas en la DB
            const dbCategories = new Set<string>();
            (data.reports || []).forEach((r: ReportItem) => {
                if (r && r.metadata?.category) {
                    dbCategories.add(r.metadata.category);
                }
            });
            // Filtrar para usar solo categorías autorizadas
            const allowed = Array.from(dbCategories).filter(cat => ['Planificación', 'Calidad', 'Subcontratos', 'SSOMA'].includes(cat));
            setCategories(prev => Array.from(new Set([...prev, ...allowed])));

            if (data.datasetError) {
                setCatalogError(data.datasetError.message || 'Error al conectar con los servicios de reportes.');
            }
        } catch (err: any) {
            console.error(err);
            setCatalogError(err.message || 'Error de comunicación con el conector de datos.');
            
            // Instrumentación temporal de diagnóstico mínima mediante alert()
            (async () => {
                try {
                    let status = "Desconocido";
                    let body = "Ninguno";
                    if (err.context && typeof err.context === 'object') {
                        if ('status' in err.context) status = String(err.context.status);
                        if (typeof err.context.text === 'function') {
                            try {
                                body = await err.context.clone().text();
                            } catch {
                                body = await err.context.text();
                            }
                        }
                    }
                    alert(`[DIAGNÓSTICO BI]\nCódigo HTTP: ${status}\nMensaje: ${err.message}\nDetalles (Edge Function): ${body}`);
                } catch (e: any) {
                    console.error("Error en diagnóstico de alerta:", e);
                }
            })();
        } finally {
            setLoadingCatalog(false);
        }
    };

    useEffect(() => {
        loadCatalogData();
    }, []);

    // Filtrar reportes según permisos de obra del usuario
    const userFilteredReports = useMemo(() => {
        return reportsList.filter(r => {
            if (!r) return false;
            if (r.scope === 'corporativo') return true;
            const requiredPerms = r.metadata?.permissions || [];
            if (requiredPerms.length > 0) {
                const hasAny = requiredPerms.some(p => hasPermission(p, 'view'));
                if (!hasAny) return false;
            }

            const pIds = r.project_ids || [];
            return pIds.includes(projectInfo.id);
        });
    }, [reportsList, projectInfo.id, hasPermission]);

    // Favoritos
    const favoriteReports = useMemo(() => {
        return userFilteredReports.filter(r => favorites.includes(r.id));
    }, [userFilteredReports, favorites]);

    // Recientes
    const recentReports = useMemo(() => {
        return recentOpened
            .map(id => userFilteredReports.find(r => r.id === id))
            .filter((r): r is ReportItem => !!r);
    }, [userFilteredReports, recentOpened]);

    // Filtrado y Búsqueda en el Catálogo
    const catalogFilteredReports = useMemo(() => {
        return userFilteredReports.filter(r => {
            const matchesSearch = (r.name || '').toLowerCase().includes(searchQuery.toLowerCase());
            const cat = r.metadata?.category || getReportCategory(r.name);
            const matchesCategory = categoryFilter === 'todos' || cat === categoryFilter;
            return matchesSearch && matchesCategory;
        });
    }, [userFilteredReports, searchQuery, categoryFilter]);

    // Agrupamiento por Alcance
    const groupedReports = useMemo(() => {
        if (!groupByScope) return { 'Listado de Reportes': catalogFilteredReports };
        return {
            'Reportes Corporativos': catalogFilteredReports.filter(r => r.scope === 'corporativo'),
            'Reportes Multiobra': catalogFilteredReports.filter(r => r.scope === 'multiobra'),
            'Reportes por Obra': catalogFilteredReports.filter(r => r.scope === 'obra')
        };
    }, [catalogFilteredReports, groupByScope]);

    // Diagnóstico
    const runTechnicalDiagnostic = async () => {
        setDiagnosing(true);
        setDiagnosticData(null);
        try {
            await getFreshMsalToken();
            const result = await runPowerBiDiagnostic();
            setDiagnosticData(result);
        } catch (err: any) {
            console.error(err);
            setDiagnosticData({ error: err.message || 'Error al evaluar conectores de datos.' });
        } finally {
            setDiagnosing(false);
        }
    };

    // Inicializar variables del diseñador
    useEffect(() => {
        if (designScope === 'obra') {
            setDesignProjects([projectInfo.id]);
        } else if (designScope === 'corporativo') {
            setDesignProjects([]);
        }
    }, [designScope, projectInfo.id]);

    useEffect(() => {
        if (datasetsList.length > 0 && !designDatasetId) {
            setDesignDatasetId(datasetsList[0].id);
        }
    }, [datasetsList, designDatasetId]);

    // Registrar o actualizar reporte
    const handleReportSaved = async (powerbiReportId: string, name: string) => {
        try {
            await getFreshMsalToken();
            const scope = isDesigning ? designScope : (selectedReport?.scope || 'obra');
            const targetProjects = isDesigning
                ? (designScope === 'obra' ? [projectInfo.id] : designProjects)
                : (selectedReport?.project_ids || []);

            const metadata = {
                category: isDesigning ? designCategory : (selectedReport?.metadata?.category || 'Planificación'),
                permissions: selectedReport?.metadata?.permissions || ['rep_reportes']
            };

            const response = await registerPowerBiReport(
                powerbiReportId,
                isDesigning ? (designName || name) : name,
                scope,
                targetProjects,
                metadata
            );

            alert("¡Reporte registrado exitosamente en K-Lyapp!");
            await loadCatalogData();

            if (isDesigning) {
                setIsDesigning(false);
                setDesignName('');
                const newReport = reportsList.find(r => r.powerbi_report_id === powerbiReportId) || response;
                setSelectedReport(newReport);
                registerRecentOpen(newReport.id);
                setViewerMode('view');
                setActiveTab('viewer');
            }
        } catch (err: any) {
            console.error(err);
            alert("Error al registrar reporte: " + (err.message || err));
        }
    };

    // Iniciar edición en Administración
    const startEditReport = (report: ReportItem) => {
        setEditingReportId(report.id);
        setEditName(report.name);
        setEditScope(report.scope);
        setEditCategory(report.metadata?.category || 'Planificación');
        setEditProjects(report.project_ids || []);
        setEditPermissions(report.metadata?.permissions || ['rep_reportes']);
    };

    // Guardar cambios en Administración
    const saveReportEdits = async () => {
        if (!editingReportId) return;
        try {
            const report = reportsList.find(r => r.id === editingReportId);
            if (!report) return;

            await getFreshMsalToken();
            const targetProjects = editScope === 'obra' ? [projectInfo.id] : (editScope === 'corporativo' ? [] : editProjects);

            await registerPowerBiReport(
                report.powerbi_report_id,
                editName,
                editScope,
                targetProjects,
                {
                    category: editCategory,
                    permissions: editPermissions
                }
            );

            alert("Reporte actualizado exitosamente.");
            setEditingReportId(null);
            await loadCatalogData();
        } catch (err: any) {
            console.error(err);
            alert("Error al actualizar reporte: " + (err.message || err));
        }
    };

    // Eliminar reporte
    const handleReportDelete = async (reportId: string) => {
        if (window.confirm("¿Seguro que deseas eliminar permanentemente este reporte de K-Lyapp?")) {
            try {
                await getFreshMsalToken();
                await deletePowerBiReport(reportId);
                alert("Reporte eliminado exitosamente.");
                if (selectedReport?.id === reportId) {
                    setSelectedReport(null);
                }
                loadCatalogData();
            } catch (err: any) {
                console.error(err);
                alert("Error al eliminar reporte: " + (err.message || err));
            }
        }
    };

    const isPlatformAdmin = currentUser?.globalRole === 'Administrador' || currentUser?.globalRole === 'SYSADMIN';

    return (
        <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900 overflow-hidden font-display">
            {/* Header del Centro de Datos */}
            <header className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 px-8 py-5 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 shrink-0 shadow-sm z-10">
                <div>
                    <h1 className="text-xl font-extrabold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
                        <span className="material-symbols-outlined text-primary text-[26px]">analytics</span>
                        Centro de Datos (Power BI 
                        <svg className="size-[16px] inline-block align-middle ml-1" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="4" y="16" width="6" height="12" rx="1.5" fill="#E6A100" />
                            <rect x="13" y="9" width="6" height="19" rx="1.5" fill="#F2C811" />
                            <rect x="22" y="2" width="6" height="26" rx="1.5" fill="#F9E03E" />
                        </svg>
                        )
                    </h1>
                    <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400 mt-0.5">
                        Administración centralizada de reportes y control de gestión de K-Lyapp
                    </p>
                </div>

                {/* Tabs del Módulo */}
                <nav className="flex items-center gap-1.5 bg-slate-100 dark:bg-slate-900 p-1 rounded-2xl border border-slate-200/50 dark:border-slate-700/50">
                    <button
                        onClick={() => { setActiveTab('home'); setIsDesigning(false); }}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                            activeTab === 'home'
                                ? 'bg-white dark:bg-slate-800 text-primary shadow-sm'
                                : 'text-slate-655 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                        }`}
                    >
                        <span className="material-symbols-outlined text-[18px]">home</span>
                        Inicio
                    </button>
                    <button
                        onClick={() => { setActiveTab('catalog'); setIsDesigning(false); }}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                            activeTab === 'catalog'
                                ? 'bg-white dark:bg-slate-800 text-primary shadow-sm'
                                : 'text-slate-655 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                        }`}
                    >
                        <span className="material-symbols-outlined text-[18px]">view_list</span>
                        Catálogo
                    </button>
                    <button
                        onClick={() => { setActiveTab('viewer'); setIsDesigning(false); }}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                            activeTab === 'viewer'
                                ? 'bg-white dark:bg-slate-800 text-primary shadow-sm'
                                : 'text-slate-655 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                        }`}
                    >
                        <span className="material-symbols-outlined text-[18px]">open_in_new</span>
                        Visor
                    </button>
                    {hasPermission('rep_disenar', 'view') && (
                        <button
                            onClick={() => { setActiveTab('designer'); setIsDesigning(false); }}
                            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                                activeTab === 'designer'
                                    ? 'bg-white dark:bg-slate-800 text-primary shadow-sm'
                                    : 'text-slate-655 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                            }`}
                        >
                            <span className="material-symbols-outlined text-[18px]">dashboard_customize</span>
                            Diseñador
                        </button>
                    )}
                    {isPlatformAdmin && (
                        <button
                            onClick={() => { setActiveTab('admin'); setIsDesigning(false); }}
                            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                                activeTab === 'admin'
                                    ? 'bg-white dark:bg-slate-800 text-primary shadow-sm'
                                    : 'text-slate-655 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                            }`}
                        >
                            <span className="material-symbols-outlined text-[18px]">settings_accessibility</span>
                            Administración
                        </button>
                    )}
                    {isPlatformAdmin && (
                        <button
                            onClick={() => { setActiveTab('diagnose'); setIsDesigning(false); runTechnicalDiagnostic(); }}
                            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                                activeTab === 'diagnose'
                                    ? 'bg-white dark:bg-slate-800 text-primary shadow-sm'
                                    : 'text-slate-655 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                            }`}
                        >
                            <span className="material-symbols-outlined text-[18px]">settings_suggest</span>
                            Diagnóstico
                        </button>
                    )}
                </nav>
            </header>

            {/* Contenedor de Pantallas */}
            <main className="flex-1 overflow-y-auto p-8 relative">

                {/* 1. PESTAÑA INICIO */}
                {activeTab === 'home' && (
                    <div className="max-w-6xl mx-auto space-y-8 animate-fade-in">
                        {/* Tarjeta de Bienvenida */}
                        <div className="bg-gradient-to-r from-primary/10 via-blue-500/5 to-transparent border border-primary/20 p-6 rounded-3xl flex items-center justify-between">
                            <div className="space-y-1">
                                <h2 className="text-lg font-black text-slate-888 dark:text-white">Bienvenido a la Reportería de K-Lyapp</h2>
                                <p className="text-xs text-slate-500 dark:text-slate-400 max-w-xl">
                                    Accede a los reportes de tus obras y administra la información de manera centralizada.
                                </p>
                            </div>
                            <div className="hidden lg:flex items-center gap-2">
                                <button
                                    onClick={() => setActiveTab('catalog')}
                                    className="px-4 py-2 bg-primary text-white text-xs font-bold rounded-xl shadow-md hover:scale-[1.02] transition-all"
                                >
                                    Abrir Catálogo
                                </button>
                            </div>
                        </div>

                        {/* Fila de Favoritos y Recientes */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Favoritos */}
                            <div className="lg:col-span-2 space-y-4">
                                <h3 className="text-xs font-extrabold text-slate-450 uppercase tracking-widest flex items-center gap-1.5">
                                    <span className="material-symbols-outlined text-amber-500 text-[18px]">star</span>
                                    Reportes Favoritos
                                </h3>

                                {favoriteReports.length === 0 ? (
                                    <div className="bg-white dark:bg-slate-800 p-8 rounded-3xl border border-slate-200/50 dark:border-slate-700/50 text-center space-y-2">
                                        <p className="text-xs font-semibold text-slate-400">Aún no has agregado reportes a tus favoritos.</p>
                                        <p className="text-[10px] text-slate-400">Presiona la estrella en el catálogo para verlos de acceso rápido aquí.</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {favoriteReports.map(r => (
                                            <div 
                                                key={r.id} 
                                                className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-200/50 dark:border-slate-700/50 hover:border-primary/50 transition-all flex flex-col justify-between h-32 cursor-pointer group"
                                                onClick={() => {
                                                    setSelectedReport(r);
                                                    registerRecentOpen(r.id);
                                                    setViewerMode('view');
                                                    setActiveTab('viewer');
                                                }}
                                            >
                                                <div className="flex justify-between items-start">
                                                    <span className="text-[9px] font-black uppercase bg-slate-105 dark:bg-slate-900 px-2 py-0.5 rounded text-slate-500">
                                                        {r.metadata?.category || getReportCategory(r.name)}
                                                    </span>
                                                    <button 
                                                        onClick={(e) => { e.stopPropagation(); toggleFavorite(r.id); }}
                                                        className="text-amber-500 hover:scale-110 transition-transform"
                                                    >
                                                        <span className="material-symbols-outlined text-[20px] fill-current">star</span>
                                                    </button>
                                                </div>
                                                <h4 className="text-xs font-bold text-slate-800 dark:text-white group-hover:text-primary transition-colors line-clamp-2">
                                                    {r.name}
                                                </h4>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Recientes */}
                            <div className="space-y-4">
                                <h3 className="text-xs font-extrabold text-slate-450 uppercase tracking-widest flex items-center gap-1.5">
                                    <span className="material-symbols-outlined text-primary text-[18px]">history</span>
                                    Reportes Recientes
                                </h3>

                                <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200/50 dark:border-slate-700/50 p-4 space-y-2">
                                    {recentReports.length === 0 ? (
                                        <p className="text-xs text-slate-450 text-center py-6">No has abierto reportes recientemente en esta sesión.</p>
                                    ) : (
                                        recentReports.map(r => (
                                            <div 
                                                key={r.id} 
                                                className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-55 dark:hover:bg-slate-900 cursor-pointer transition-all group"
                                                onClick={() => {
                                                    setSelectedReport(r);
                                                    registerRecentOpen(r.id);
                                                    setViewerMode('view');
                                                    setActiveTab('viewer');
                                                }}
                                            >
                                                <div className="p-2 bg-slate-100 dark:bg-slate-900 rounded-lg text-slate-500 group-hover:text-primary">
                                                    <span className="material-symbols-outlined text-[18px]">analytics</span>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate group-hover:text-primary">{r.name}</h4>
                                                    <p className="text-[9px] text-slate-400 uppercase font-semibold">{r.scope === 'corporativo' ? 'Corporativo' : r.scope === 'multiobra' ? 'Multiobra' : 'Por Obra'}</p>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Accesos Rápidos y Estado del Servicio */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Accesos Rápidos */}
                            <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-slate-200/50 dark:border-slate-700/50 space-y-4 md:col-span-2">
                                <h3 className="text-xs font-extrabold text-slate-450 uppercase tracking-widest flex items-center gap-1.5">
                                    <span className="material-symbols-outlined text-primary text-[18px]">bolt</span>
                                    Accesos por Módulo
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <button 
                                        onClick={() => { setCategoryFilter('Planificación'); setActiveTab('catalog'); }}
                                        className="p-3 bg-slate-50 dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-900/60 rounded-xl text-left border border-slate-100 dark:border-slate-800 transition-all flex items-center gap-3 group"
                                    >
                                        <span className="material-symbols-outlined text-blue-500">trending_up</span>
                                        <div>
                                            <h4 className="text-xs font-bold text-slate-800 dark:text-white">Planificación</h4>
                                            <p className="text-[9px] text-slate-400">Curvas de avance físico y plazos de obra</p>
                                        </div>
                                    </button>
                                    <button 
                                        onClick={() => { setCategoryFilter('Calidad'); setActiveTab('catalog'); }}
                                        className="p-3 bg-slate-50 dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-900/60 rounded-xl text-left border border-slate-100 dark:border-slate-800 transition-all flex items-center gap-3 group"
                                    >
                                        <span className="material-symbols-outlined text-emerald-500">verified</span>
                                        <div>
                                            <h4 className="text-xs font-bold text-slate-800 dark:text-white">Calidad</h4>
                                            <p className="text-[9px] text-slate-400">Auditorías de protocolos y no conformidades</p>
                                        </div>
                                    </button>
                                    <button 
                                        onClick={() => { setCategoryFilter('Subcontratos'); setActiveTab('catalog'); }}
                                        className="p-3 bg-slate-50 dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-900/60 rounded-xl text-left border border-slate-100 dark:border-slate-800 transition-all flex items-center gap-3 group"
                                    >
                                        <span className="material-symbols-outlined text-amber-500">payments</span>
                                        <div>
                                            <h4 className="text-xs font-bold text-slate-800 dark:text-white">Subcontratos</h4>
                                            <p className="text-[9px] text-slate-400">Estados de Pago y montos contratados</p>
                                        </div>
                                    </button>
                                    <button 
                                        onClick={() => { setCategoryFilter('SSOMA'); setActiveTab('catalog'); }}
                                        className="p-3 bg-slate-50 dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-900/60 rounded-xl text-left border border-slate-100 dark:border-slate-800 transition-all flex items-center gap-3 group"
                                    >
                                        <span className="material-symbols-outlined text-purple-500">shield</span>
                                        <div>
                                            <h4 className="text-xs font-bold text-slate-800 dark:text-white">SSOMA</h4>
                                            <p className="text-[9px] text-slate-400">Seguridad, Charlas e incidentes</p>
                                        </div>
                                    </button>
                                </div>
                            </div>

                            {/* Estado del Servicio */}
                            <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-slate-200/50 dark:border-slate-700/50 space-y-4 flex flex-col justify-between">
                                <h3 className="text-xs font-extrabold text-slate-450 uppercase tracking-widest flex items-center gap-1.5">
                                    <span className="material-symbols-outlined text-emerald-500 text-[18px]">dns</span>
                                    Servicios de Reportes
                                </h3>
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center text-xs">
                                        <span className="text-slate-500">Servidor de Consultas:</span>
                                        <span className="flex items-center gap-1.5 text-emerald-600 font-bold">
                                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-ping"></span> Operacional
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs">
                                        <span className="text-slate-500">Conector Azure:</span>
                                        <span className="text-emerald-600 font-bold">Activo</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs">
                                        <span className="text-slate-500">Persistencia local:</span>
                                        <span className="text-slate-500 font-bold">Sincronizado</span>
                                    </div>
                                </div>
                                <div className="bg-slate-50 dark:bg-slate-900/50 p-2.5 rounded-xl border border-slate-100 dark:border-slate-800 text-[10px] text-slate-400 text-center leading-normal">
                                    Conexión corporativa enlazada con las credenciales de la obra.
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* 2. PESTAÑA CATÁLOGO */}
                {activeTab === 'catalog' && (
                    <div className="max-w-5xl mx-auto w-full space-y-6 animate-fade-in">
                        {/* Panel de Filtros, Búsqueda y Agrupamiento */}
                        <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-200/50 dark:border-slate-700/50 shadow-sm flex flex-col md:flex-row gap-4 justify-between items-center">
                            <div className="relative w-full md:w-80">
                                <span className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">search</span>
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Buscar reporte de obra..."
                                    className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-900 text-xs border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-1 focus:ring-primary transition-all text-slate-888 dark:text-white font-medium"
                                />
                            </div>

                            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                                {/* Selector de Categoría */}
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-black uppercase text-slate-450">Módulo:</span>
                                    <select
                                        value={categoryFilter}
                                        onChange={(e) => setCategoryFilter(e.target.value)}
                                        className="px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-250 dark:border-slate-700 rounded-xl text-[10px] font-black uppercase tracking-wider text-slate-655 dark:text-slate-300 cursor-pointer focus:ring-1 focus:ring-primary outline-none"
                                    >
                                        <option value="todos">Todos</option>
                                        {categories.map(c => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Toggle de Agrupamiento */}
                                <button
                                    onClick={() => setGroupByScope(!groupByScope)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase border transition-all ${
                                        groupByScope 
                                            ? 'bg-primary border-primary text-white shadow-sm' 
                                            : 'bg-white dark:bg-slate-900 border-slate-250 dark:border-slate-700 text-slate-655 dark:text-slate-300 hover:bg-slate-50'
                                    }`}
                                >
                                    <span className="material-symbols-outlined text-[14px]">table_rows</span>
                                    Agrupar Obras
                                </button>
                            </div>
                        </div>

                        {/* Listado de Reportes Agrupados / Planos */}
                        {loadingCatalog ? (
                            <div className="flex flex-col items-center justify-center py-16 space-y-3">
                                <span className="material-symbols-outlined animate-spin text-primary text-4xl">progress_activity</span>
                                <p className="text-xs font-bold text-slate-500">Cargando catálogo de reportes...</p>
                            </div>
                        ) : catalogFilteredReports.length === 0 ? (
                            <div className="bg-white dark:bg-slate-800 rounded-3xl p-16 text-center border border-slate-200/50 dark:border-slate-700/50 max-w-md mx-auto space-y-4">
                                <div className="size-16 rounded-2xl bg-slate-100 dark:bg-slate-900 text-slate-400 flex items-center justify-center mx-auto">
                                    <span className="material-symbols-outlined text-[32px]">folder_off</span>
                                </div>
                                <h4 className="text-sm font-extrabold text-slate-800 dark:text-white">Sin Reportes</h4>
                                <p className="text-xs text-slate-400 max-w-xs mx-auto">
                                    No hay reportes disponibles en este módulo para la obra actual.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-8">
                                {Object.entries(groupedReports).map(([groupTitle, reports]) => (
                                    reports.length > 0 && (
                                        <div key={groupTitle} className="space-y-4">
                                            <h3 className="text-[10px] font-black text-slate-450 uppercase tracking-widest pl-1">{groupTitle} ({reports.length})</h3>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                {reports.map(r => (
                                                    <div 
                                                        key={r.id}
                                                        className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200/60 dark:border-slate-700/60 p-5 hover:shadow-md hover:border-primary/50 cursor-pointer transition-all flex flex-col justify-between h-44 group"
                                                        onClick={() => {
                                                            setSelectedReport(r);
                                                            registerRecentOpen(r.id);
                                                            setViewerMode('view');
                                                            setActiveTab('viewer');
                                                        }}
                                                    >
                                                        <div className="space-y-3">
                                                            <div className="flex justify-between items-start">
                                                                <span className="text-[8px] font-black uppercase px-2 py-0.5 bg-slate-100 dark:bg-slate-900 rounded text-slate-500">
                                                                    {r.metadata?.category || getReportCategory(r.name)}
                                                                </span>
                                                                <button 
                                                                    onClick={(e) => { e.stopPropagation(); toggleFavorite(r.id); }}
                                                                    className="text-slate-350 dark:text-slate-655 hover:text-amber-500 transition-colors"
                                                                >
                                                                    <span className={`material-symbols-outlined text-[20px] ${favorites.includes(r.id) ? 'text-amber-500 fill-current' : ''}`}>
                                                                        star
                                                                    </span>
                                                                </button>
                                                            </div>
                                                            <h4 className="text-xs font-black text-slate-850 dark:text-white leading-normal group-hover:text-primary transition-colors line-clamp-2">
                                                                {r.name}
                                                            </h4>
                                                        </div>
                                                        <div className="flex justify-between items-center border-t border-slate-100 dark:border-slate-700/50 pt-3">
                                                            <span className="text-[8px] text-slate-400 font-bold uppercase tracking-wider">
                                                                {r.scope === 'corporativo' ? 'Corporativo' : r.scope === 'multiobra' ? 'Multiobra' : 'Por Obra'}
                                                            </span>
                                                            <span className="material-symbols-outlined text-slate-350 text-[18px] group-hover:translate-x-1 transition-transform">
                                                                arrow_right_alt
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* 3. PESTAÑA VISOR */}
                {activeTab === 'viewer' && (
                    <div className="h-full flex flex-col gap-6 animate-fade-in">
                        {!selectedReport ? (
                            <div className="max-w-md mx-auto w-full text-center py-20 space-y-4 bg-white dark:bg-slate-800 rounded-3xl border border-slate-200/50 p-8">
                                <div className="size-16 bg-slate-50 dark:bg-slate-900 text-slate-400 flex items-center justify-center mx-auto rounded-2xl">
                                    <span className="material-symbols-outlined text-[32px]">open_in_new</span>
                                </div>
                                <div>
                                    <h4 className="text-sm font-extrabold text-slate-850 dark:text-white">Visor Desocupado</h4>
                                    <p className="text-xs text-slate-400 max-w-xs mx-auto mt-1">
                                        Selecciona un reporte en el Catálogo o en Inicio para abrirlo.
                                    </p>
                                </div>
                                <button 
                                    onClick={() => setActiveTab('catalog')}
                                    className="px-4 py-2 bg-primary text-white text-xs font-bold rounded-xl"
                                >
                                    Abrir Catálogo
                                </button>
                            </div>
                        ) : (
                            /* Visor Seguro de K-LyDApp */
                            <div className={`flex flex-col h-full bg-white dark:bg-slate-800 rounded-3xl overflow-hidden border border-slate-200/80 dark:border-slate-700/80 shadow-sm relative ${
                                isFullscreen ? 'fixed inset-0 z-50 rounded-none' : 'h-[750px]'
                            }`}>
                                <div className="px-6 py-4 border-b border-slate-150 dark:border-slate-700 flex items-center justify-between shrink-0 bg-slate-50 dark:bg-slate-800/90 z-20">
                                    <div className="flex items-center gap-3">
                                        <button 
                                            onClick={() => setSelectedReport(null)}
                                            className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl text-slate-500 hover:text-slate-800 transition-colors"
                                            title="Cerrar Visor"
                                        >
                                            <span className="material-symbols-outlined text-[20px]">close</span>
                                        </button>
                                        <div>
                                            <h4 className="text-xs font-black text-slate-850 dark:text-white truncate max-w-xs">{selectedReport.name}</h4>
                                            <p className="text-[8px] text-slate-400 uppercase font-extrabold tracking-wider mt-0.5">
                                                Módulo: {selectedReport.metadata?.category || getReportCategory(selectedReport.name)} • Obra: {selectedReport.scope === 'corporativo' ? 'Corporativo' : selectedReport.scope === 'multiobra' ? 'Multiobra' : 'Solo esta Obra'}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Barra de Controles: Refresh, Editar, Fullscreen */}
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setEmbedRefreshKey(prev => prev + 1)}
                                            className="p-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 rounded-lg text-slate-600 dark:text-slate-400"
                                            title="Actualizar Datos de Obra"
                                        >
                                            <span className="material-symbols-outlined text-[18px]">refresh</span>
                                        </button>

                                        {hasPermission('rep_disenar', 'edit') && (
                                            <button
                                                onClick={() => setViewerMode(viewerMode === 'view' ? 'edit' : 'view')}
                                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase transition-all border ${
                                                    viewerMode === 'edit'
                                                        ? 'bg-orange-500 border-orange-500 text-white hover:bg-orange-600'
                                                        : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300'
                                                }`}
                                            >
                                                <span className="material-symbols-outlined text-[14px]">
                                                    {viewerMode === 'edit' ? 'visibility' : 'edit'}
                                                </span>
                                                {viewerMode === 'edit' ? 'Modo Lectura' : 'Modo Edición'}
                                            </button>
                                        )}

                                        <button
                                            onClick={() => setIsFullscreen(!isFullscreen)}
                                            className="p-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 rounded-lg text-slate-600 dark:text-slate-400"
                                            title="Pantalla Completa"
                                        >
                                            <span className="material-symbols-outlined text-[18px]">
                                                {isFullscreen ? 'fullscreen_exit' : 'fullscreen'}
                                            </span>
                                        </button>
                                    </div>
                                </div>

                                {/* Contenedor Embed de Power BI */}
                                <div className="flex-1 relative overflow-hidden bg-slate-100 dark:bg-slate-900">
                                    <PowerBIEmbedContainer
                                        key={embedRefreshKey}
                                        currentUser={currentUser}
                                        canView={hasPermission('rep_reportes', 'view')}
                                        canDesign={hasPermission('rep_disenar', 'view')}
                                        canModifyDesign={hasPermission('rep_disenar', 'edit')}
                                        initialMode={viewerMode}
                                        action={viewerMode}
                                        reportId={selectedReport.powerbi_report_id}
                                        context="consumption"
                                        onReportSaved={handleReportSaved}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* 4. PESTAÑA DISEÑADOR */}
                {activeTab === 'designer' && (
                    <div className="max-w-4xl mx-auto w-full animate-fade-in">
                        {!isDesigning ? (
                            <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200/50 dark:border-slate-700/50 shadow-sm p-8 space-y-6 max-w-xl mx-auto">
                                <div className="space-y-1 text-center">
                                    <div className="size-14 bg-amber-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                        <svg className="size-[28px]" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <rect x="4" y="16" width="6" height="12" rx="1.5" fill="#E6A100" />
                                            <rect x="13" y="9" width="6" height="19" rx="1.5" fill="#F2C811" />
                                            <rect x="22" y="2" width="6" height="26" rx="1.5" fill="#F9E03E" />
                                        </svg>
                                    </div>
                                    <h3 className="text-base font-black text-slate-850 dark:text-white">Crear Reporte Personalizado</h3>
                                    <p className="text-[10px] text-slate-500">
                                        Construye visualizaciones para la obra a partir de las fuentes autorizadas.
                                    </p>
                                </div>

                                <div className="space-y-4">
                                    {/* Nombre */}
                                    <div className="space-y-1">
                                        <label className="text-[9px] font-black text-slate-450 uppercase tracking-widest block ml-1">Nombre del Reporte</label>
                                        <input
                                            type="text"
                                            value={designName}
                                            onChange={(e) => setDesignName(e.target.value)}
                                            className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-250 dark:border-slate-750 rounded-xl text-xs outline-none focus:ring-1 focus:ring-primary transition-all text-slate-850 dark:text-white font-medium"
                                            placeholder="Ej: Curva S Hitos Hito 2"
                                        />
                                    </div>

                                    {/* Fuente de Datos (Dataset) */}
                                    <div className="space-y-1">
                                        <label className="text-[9px] font-black text-slate-450 uppercase tracking-widest block ml-1">Fuente de datos</label>
                                        <select
                                            value={designDatasetId}
                                            onChange={(e) => setDesignDatasetId(e.target.value)}
                                            className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-250 dark:border-slate-750 rounded-xl text-xs outline-none focus:ring-1 focus:ring-primary transition-all text-slate-850 dark:text-white font-medium cursor-pointer"
                                        >
                                            {datasetsList.map(d => (
                                                <option key={d.id} value={d.id}>{d.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Módulo */}
                                    <div className="space-y-1">
                                        <label className="text-[9px] font-black text-slate-450 uppercase tracking-widest block ml-1">Módulo</label>
                                        <select
                                            value={designCategory}
                                            onChange={(e) => setDesignCategory(e.target.value)}
                                            className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-250 dark:border-slate-750 rounded-xl text-xs outline-none focus:ring-1 focus:ring-primary transition-all text-slate-850 dark:text-white font-medium cursor-pointer"
                                        >
                                            {categories.map(c => (
                                                <option key={c} value={c}>{c}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Obra (Scope) */}
                                    <div className="space-y-1">
                                        <label className="text-[9px] font-black text-slate-450 uppercase tracking-widest block ml-1">Obra</label>
                                        <select
                                            value={designScope}
                                            onChange={(e) => setDesignScope(e.target.value as any)}
                                            className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-250 dark:border-slate-750 rounded-xl text-xs outline-none focus:ring-1 focus:ring-primary transition-all text-slate-850 dark:text-white font-medium cursor-pointer"
                                        >
                                            <option value="obra">Solo esta Obra ({projectInfo.name})</option>
                                            <option value="multiobra">Multiobra (Obras seleccionadas)</option>
                                            <option value="corporativo">Corporativo (Global)</option>
                                        </select>
                                    </div>

                                    {/* Proyectos Multiobra */}
                                    {designScope === 'multiobra' && (
                                        <div className="space-y-2 border border-slate-200 dark:border-slate-700 p-4 rounded-2xl bg-slate-50 dark:bg-slate-900 max-h-40 overflow-y-auto">
                                            <label className="text-[9px] font-black text-slate-450 uppercase tracking-widest block mb-2">Obras autorizadas:</label>
                                            {projectsList.map(proj => (
                                                <label key={proj.project_id} className="flex items-center gap-2 p-1.5 hover:bg-slate-100 rounded-lg cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={designProjects.includes(proj.project_id)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setDesignProjects([...designProjects, proj.project_id]);
                                                            } else {
                                                                setDesignProjects(designProjects.filter(id => id !== proj.project_id));
                                                            }
                                                        }}
                                                        className="rounded text-primary h-3.5 w-3.5"
                                                    />
                                                    <span className="text-[10px] font-bold text-slate-700 dark:text-slate-300">{proj.name}</span>
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <button
                                    onClick={() => setIsDesigning(true)}
                                    disabled={!designName.trim() || !designDatasetId}
                                    className="w-full py-4 bg-primary text-white rounded-xl text-xs font-black tracking-widest uppercase transition-all shadow-md shadow-primary/20 disabled:opacity-50 disabled:shadow-none hover:scale-[1.01]"
                                >
                                    Iniciar Lienzo de Creación
                                </button>
                            </div>
                        ) : (
                            /* Lienzo de Diseño */
                            <div className="flex flex-col h-[750px] bg-white dark:bg-slate-800 rounded-3xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-sm relative">
                                <div className="px-6 py-4 border-b border-slate-150 dark:border-slate-700 flex items-center justify-between shrink-0 bg-slate-50 dark:bg-slate-800/90 z-20">
                                    <div className="flex items-center gap-3">
                                        <span className="animate-pulse h-2 w-2 rounded-full bg-orange-500"></span>
                                        <div>
                                            <p className="text-[9px] font-black text-orange-600 uppercase tracking-wider">Diseñador Activo</p>
                                            <h4 className="text-xs font-black text-slate-850 dark:text-white">{designName}</h4>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => {
                                            if (window.confirm("¿Seguro que deseas salir del Diseñador? Los cambios no guardados se perderán.")) {
                                                setIsDesigning(false);
                                                setDesignName('');
                                            }
                                        }}
                                        className="px-3 py-1.5 border border-slate-200 rounded-lg text-[10px] font-black uppercase text-slate-500"
                                    >
                                        Cancelar
                                    </button>
                                </div>
                                <div className="flex-1 relative overflow-hidden bg-slate-100 dark:bg-slate-900">
                                    <PowerBIEmbedContainer
                                        currentUser={currentUser}
                                        canView={hasPermission('rep_reportes', 'view')}
                                        canDesign={hasPermission('rep_disenar', 'view')}
                                        canModifyDesign={hasPermission('rep_disenar', 'edit')}
                                        initialMode="edit"
                                        action="create"
                                        context="designer"
                                        datasetId={designDatasetId}
                                        onReportSaved={handleReportSaved}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* 5. PESTAÑA ADMINISTRACIÓN */}
                {activeTab === 'admin' && (
                    <div className="max-w-6xl mx-auto w-full space-y-6 animate-fade-in">
                        {/* Tabla de Catálogo y Configuración de Alcances */}
                        <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-slate-200/50 dark:border-slate-700/50 shadow-sm space-y-4">
                            <h3 className="text-xs font-extrabold text-slate-850 dark:text-white uppercase tracking-wider">
                                Catálogo General de Reportes K-Lyapp
                            </h3>

                            {editingReportId ? (
                                /* Formulario de Edición */
                                <div className="border border-slate-200 dark:border-slate-700 p-5 rounded-2xl space-y-4">
                                    <h4 className="text-xs font-black uppercase text-orange-600">Configurar Alcance de Reporte</h4>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="text-[9px] font-bold block mb-1">Nombre</label>
                                            <input
                                                type="text"
                                                value={editName}
                                                onChange={(e) => setEditName(e.target.value)}
                                                className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border rounded-xl text-xs outline-none"
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-[9px] font-bold block mb-1">Módulo</label>
                                                <select
                                                    value={editCategory}
                                                    onChange={(e) => setEditCategory(e.target.value)}
                                                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border rounded-xl text-xs cursor-pointer"
                                                >
                                                    {categories.map(c => (
                                                        <option key={c} value={c}>{c}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="text-[9px] font-bold block mb-1">Obra (Alcance)</label>
                                                <select
                                                    value={editScope}
                                                    onChange={(e) => setEditScope(e.target.value as any)}
                                                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border rounded-xl text-xs cursor-pointer"
                                                >
                                                    <option value="obra">Solo esta Obra</option>
                                                    <option value="multiobra">Multiobra</option>
                                                    <option value="corporativo">Corporativo</option>
                                                </select>
                                            </div>
                                        </div>

                                        {/* Obras Multiobra */}
                                        {editScope === 'multiobra' && (
                                            <div className="border p-3 rounded-xl max-h-32 overflow-y-auto">
                                                {projectsList.map(proj => (
                                                    <label key={proj.project_id} className="flex items-center gap-2 p-1">
                                                        <input
                                                            type="checkbox"
                                                            checked={editProjects.includes(proj.project_id)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setEditProjects([...editProjects, proj.project_id]);
                                                                } else {
                                                                    setEditProjects(editProjects.filter(id => id !== proj.project_id));
                                                                }
                                                            }}
                                                            className="rounded text-primary h-3 w-3"
                                                        />
                                                        <span className="text-[10px] font-bold text-slate-700">{proj.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex gap-2 justify-end">
                                        <button 
                                            onClick={() => setEditingReportId(null)}
                                            className="px-3 py-1.5 border rounded-xl text-xs font-bold text-slate-500"
                                        >
                                            Cancelar
                                        </button>
                                        <button 
                                            onClick={saveReportEdits}
                                            className="px-4 py-1.5 bg-primary text-white rounded-xl text-xs font-bold"
                                        >
                                            Guardar Cambios
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                /* Listado General */
                                <div className="overflow-x-auto rounded-2xl border border-slate-150 dark:border-slate-700/80">
                                    <table className="w-full text-left text-xs">
                                        <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-150 text-[9px] font-bold text-slate-500 uppercase tracking-wider">
                                            <tr>
                                                <th className="px-4 py-3">Nombre</th>
                                                <th className="px-4 py-3">Módulo</th>
                                                <th className="px-4 py-3">Obra</th>
                                                <th className="px-4 py-3 text-right">Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-150 dark:divide-slate-700/50">
                                            {reportsList.map(r => (
                                                <tr key={r.id} className="hover:bg-slate-50/50 transition-colors">
                                                    <td className="px-4 py-3 font-bold text-slate-800 dark:text-white max-w-[180px] truncate">{r.name}</td>
                                                    <td className="px-4 py-3 text-slate-500">{r.metadata?.category || getReportCategory(r.name)}</td>
                                                    <td className="px-4 py-3">
                                                        <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase ${
                                                            r.scope === 'corporativo' ? 'bg-purple-100 text-purple-800' :
                                                            r.scope === 'multiobra' ? 'bg-emerald-100 text-emerald-800' :
                                                            'bg-blue-100 text-blue-800'
                                                        }`}>
                                                            {r.scope === 'corporativo' ? 'Corporativo' : r.scope === 'multiobra' ? 'Multiobra' : 'Por Obra'}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-right space-x-2">
                                                        <button 
                                                            onClick={() => startEditReport(r)}
                                                            className="text-primary hover:underline font-bold"
                                                        >
                                                            Configurar
                                                        </button>
                                                        <button 
                                                            onClick={() => handleReportDelete(r.id)}
                                                            className="text-red-500 hover:underline font-bold"
                                                        >
                                                            Borrar
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* 6. PESTAÑA DIAGNÓSTICO */}
                {activeTab === 'diagnose' && (
                    <div className="max-w-4xl mx-auto w-full space-y-6 animate-fade-in">
                        <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200/50 dark:border-slate-700/50 shadow-sm p-6 space-y-6">
                            <div className="flex justify-between items-center border-b border-slate-100 dark:border-slate-750 pb-3">
                                <div>
                                    <h3 className="text-sm font-extrabold text-slate-850 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
                                        <span className="material-symbols-outlined text-primary text-[20px]">settings_suggest</span>
                                        Diagnóstico del Conector de Datos
                                    </h3>
                                    <p className="text-[10px] text-slate-500 mt-0.5">
                                        Auditoría técnica del canal de comunicación con el motor de reportes de Azure.
                                    </p>
                                </div>
                                <button
                                    onClick={runTechnicalDiagnostic}
                                    disabled={diagnosing}
                                    className="px-4 py-2 bg-primary text-white text-xs font-bold rounded-xl disabled:opacity-50 flex items-center gap-1.5"
                                >
                                    <span className={`material-symbols-outlined text-[16px] ${diagnosing ? 'animate-spin' : ''}`}>sync</span>
                                    Re-evaluar
                                </button>
                            </div>

                            {diagnosing ? (
                                <div className="flex flex-col items-center justify-center py-16 space-y-3">
                                    <span className="material-symbols-outlined animate-spin text-primary text-4xl">progress_activity</span>
                                    <p className="text-xs font-bold text-slate-500">Comprobando conector de datos...</p>
                                </div>
                            ) : diagnosticData ? (
                                <div className="space-y-6 text-xs">
                                    {/* Grilla de Estados */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Azure / OAuth */}
                                        <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 space-y-2">
                                            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block">Autenticación de Credenciales Azure</span>
                                            <div className="flex items-center justify-between">
                                                <span className="font-bold text-slate-800 dark:text-white">Estado Conector OAuth:</span>
                                                <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${
                                                    diagnosticData.oauthAuthentication === 'ÉXITO' 
                                                        ? 'bg-green-105 text-green-700' 
                                                        : 'bg-red-105 text-red-700'
                                                }`}>
                                                    {diagnosticData.oauthAuthentication || 'DESCONOCIDO'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Supabase Edge Function */}
                                        <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 space-y-2">
                                            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block">Edge Runtime (Supabase)</span>
                                            <div className="flex items-center justify-between">
                                                <span className="font-bold text-slate-800 dark:text-white">Estado del Servicio:</span>
                                                <span className="px-2 py-0.5 rounded text-[9px] font-black uppercase bg-green-100 text-green-800">
                                                    ACTIVO (200)
                                                </span>
                                            </div>
                                        </div>

                                        {/* Workspace */}
                                        <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 space-y-2">
                                            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block">Workspace de la Obra</span>
                                            <div className="flex items-center justify-between">
                                                <span className="font-bold text-slate-800 dark:text-white">Estado de Conexión:</span>
                                                <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${
                                                    diagnosticData.workspaceQueryStatus?.includes('200') 
                                                        ? 'bg-green-100 text-green-800' 
                                                        : 'bg-amber-100 text-amber-800'
                                                }`}>
                                                    {diagnosticData.workspaceQueryStatus || 'DESCONOCIDO'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-[10px] text-slate-450 mt-1 font-mono">
                                                <span>Identificador:</span>
                                                <span>{diagnosticData.workspaceIdMasked || 'N/A'}</span>
                                            </div>
                                        </div>

                                        {/* Fuentes de Datos */}
                                        <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 space-y-2">
                                            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block">Fuentes de Datos Activas</span>
                                            <div className="flex items-center justify-between">
                                                <span className="font-bold text-slate-800 dark:text-white">Conteo:</span>
                                                <span className="px-2 py-0.5 rounded text-[9px] font-black uppercase bg-blue-100 text-blue-800">
                                                    {diagnosticData.datasetsCount || 0}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Mapeo de Variables */}
                                    <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 space-y-3">
                                        <h4 className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                                            Variables del Canal Analítico
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 font-mono">
                                            {diagnosticData.variables && Object.entries(diagnosticData.variables).map(([key, val]) => (
                                                <div 
                                                    key={key} 
                                                    className="flex items-center justify-between p-2.5 bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700/60"
                                                >
                                                    <span className="text-slate-450 text-[10px] font-bold">{key.replace('POWERBI_', '')}:</span>
                                                    <span className={`px-2.5 py-0.5 rounded font-black text-[9px] uppercase ${
                                                        val === 'PRESENTE' 
                                                            ? 'bg-green-50 text-green-700' 
                                                            : 'bg-red-50 text-red-600'
                                                    }`}>
                                                        {val as string}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-xs text-slate-450 italic text-center py-6">Haz clic en Re-evaluar para auditar los servicios de datos.</p>
                            )}
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
